/* ═══════════════════════════════════════════════════
   PUSH NOTIFICATIONS — Capacitor native only
   Wires @capacitor/push-notifications to Firebase
   Cloud Messaging. Handles permission request,
   token registration, and notification routing.
═══════════════════════════════════════════════════ */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { db, auth } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Initialise push notifications on app launch.
 * Only runs on Capacitor native (Android).
 * Requests permission, registers for FCM, stores token in Firestore.
 */
export async function initPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;

  // Check current permission status
  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === 'prompt') {
    // Request permission
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive !== 'granted') {
    console.log('[Push] Permission not granted');
    return;
  }

  // Register for push notifications
  await PushNotifications.register();

  // Listen for registration success — save FCM token to Firestore
  PushNotifications.addListener('registration', async (token) => {
    console.log('[Push] FCM token:', token.value);
    await saveFCMToken(token.value);
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push] Registration error:', error);
  });

  // Notification received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[Push] Foreground notification:', notification);
    handleForegroundNotification(notification);
  });

  // Notification tapped (app was in background or killed)
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[Push] Notification tapped:', action);
    handleNotificationTap(action.notification);
  });
}

/**
 * Save FCM token to the user's Firestore doc for server-side sending.
 */
async function saveFCMToken(token) {
  const user = auth.currentUser;
  if (!user) return;

  try {
    await setDoc(doc(db, 'fcmTokens', user.uid), {
      token,
      platform: 'android',
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.error('[Push] Failed to save FCM token:', err);
  }
}

/**
 * Handle notification received while the app is in the foreground.
 * Shows a brief in-app notification banner.
 */
function handleForegroundNotification(notification) {
  // For now, log it. The app can display an in-app toast/banner if desired.
  // The notification data contains the type and payload for routing.
  const data = notification.data || {};
  console.log('[Push] Type:', data.type, 'Payload:', data);
}

/**
 * Handle notification tap — navigate to the relevant screen.
 */
function handleNotificationTap(notification) {
  const data = notification.data || {};

  switch (data.type) {
    case 'new_arc':
      // Navigate to the ARC details
      if (data.bookId && data.authorId) {
        window.location.href = `/reader?arc=${data.bookId}`;
      }
      break;

    case 'new_message':
      // Navigate to messages
      window.location.href = '/reader?tab=messages';
      break;

    case 'purchase_confirmed':
      // Navigate to the content
      if (data.contentType === 'audiobook' && data.contentId) {
        window.location.href = `/listen/${data.contentId}`;
      } else if (data.contentType === 'ebook' && data.contentId) {
        window.location.href = `/read/${data.contentId}`;
      }
      break;

    default:
      // Open reader portal
      window.location.href = '/reader';
  }
}
